import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import {
  LadderHead,
  LadderRow,
  PageHeader,
  bacenCasesHref,
  useFormats,
} from '@/pages/bacen/shared';
import { fetchBacenTimeline } from '@/services/bacen';

/**
 * How long each step takes (citation to first decision, first decision to
 * the CRSFN, the CRSFN to publication), the citation-year cohorts and the
 * disqualification terms starting and ending per year. The outcome trend is
 * refused: the youngest cohorts are mostly undecided and a trend over them
 * would read the calendar as a change of policy.
 */
export function BacenTimelinePage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'timeline'],
    queryFn: fetchBacenTimeline,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const days = (v: number) => t('bacen:timeline.days', { n: number.format(Math.round(v)) });

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:timeline" exportPath="/api/v1/bacen/export/timeline" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-intervals>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:timeline.intervals.title')}
        </h2>
        <div className="space-y-3">
          {data.intervals.map((interval, i) => {
            const step = interval.value?.step ?? (interval.reason_params?.step as string) ?? `${i}`;
            return (
              <div key={step} data-interval={step}>
                <h3 className="mb-1 text-xs font-medium text-foreground">
                  {t(`bacen:timeline.steps.${step}`, { defaultValue: step })}
                </h3>
                <Answer answer={interval} help={t('bacen:timeline.intervals.help')}>
                  {(iv) => (
                    <div className="space-y-1">
                      <div className="overflow-x-auto rounded-md border">
                        <table className="w-full text-sm">
                          <LadderHead nLabel={t('bacen:timeline.intervals.n')} />
                          <tbody>
                            <LadderRow
                              label={t('bacen:timeline.intervals.days')}
                              summary={iv.days}
                              currency={currency}
                              format={days}
                            />
                          </tbody>
                        </table>
                      </div>
                      {iv.negative > 0 ? (
                        <p className="text-xs text-muted-foreground" data-negative>
                          {t('bacen:timeline.intervals.negative', {
                            n: number.format(iv.negative),
                          })}
                        </p>
                      ) : null}
                    </div>
                  )}
                </Answer>
              </div>
            );
          })}
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:timeline.guide.intervals" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-cohorts>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:timeline.cohorts.title')}
        </h2>
        <Answer answer={data.cohorts} help={t('bacen:timeline.cohorts.help')}>
          {(cohorts) => (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('bacen:timeline.cohorts.year')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:timeline.cohorts.rows')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:timeline.cohorts.ruled')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:timeline.cohorts.pending')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:timeline.cohorts.paid')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:timeline.cohorts.owed')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((c) => (
                    <tr key={c.year} className="border-t" data-cohort={c.year}>
                      <td className="px-3 py-2 text-foreground">
                        <Link
                          to={bacenCasesHref({ year: c.year })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {c.year}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.rows)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.ruled)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.ruled_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.pending)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.paid)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.paid_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.owed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:timeline.guide.cohorts" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <RefusalCard title={t('bacen:timeline.trend.title')} answer={data.outcome_trend} />
        <section className="rounded-lg border bg-card p-4" data-register-terms>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:timeline.terms.title')}
          </h2>
          <Answer answer={data.register_terms} help={t('bacen:timeline.terms.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('bacen:timeline.terms.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:timeline.terms.starting')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:timeline.terms.ending')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.year} className="border-t" data-term-year={r.year}>
                        <td className="px-3 py-2 text-foreground">{r.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(r.starting)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(r.ending)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>
      </div>
    </div>
  );
}
